/**
 * New Arrivals Carousel
 *
 * A lightweight, dependency-free horizontal product carousel with previous / next
 * controls. The track is a native overflow-x scroller with CSS scroll snapping;
 * this element only wires up the arrow buttons and keeps their disabled / hidden
 * state in sync with the scroll position.
 *
 * Expected markup hooks (set by sections/new-arrivals-carousel.liquid):
 *   [data-track]              - the scrolling element
 *   [data-slide]              - an individual slide (used to measure the scroll step)
 *   [data-nav]                - the wrapper around the arrow buttons
 *   [data-control="previous"] - the "previous" button
 *   [data-control="next"]     - the "next" button
 */
class NewArrivalsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.nav = this.querySelector('[data-nav]');
    this.previousButton = this.querySelector('[data-control="previous"]');
    this.nextButton = this.querySelector('[data-control="next"]');

    if (!this.track) return;

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.updateControls, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.updateControls);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.updateControls);
    }

    this.updateControls();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.updateControls);
  }

  /** Width of a single slide including the inter-slide gap. */
  get slideStep() {
    if (!this.track) return 0;

    const slide = this.track.querySelector('[data-slide]');
    if (!slide) return this.track.clientWidth;

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    return slide.getBoundingClientRect().width + gap;
  }

  /** How far to move per click: a "page" of slides, keeping one slide of context. */
  get scrollDistance() {
    const step = this.slideStep;
    if (!step) return this.track?.clientWidth ?? 0;

    const visible = Math.max(1, Math.round((this.track?.clientWidth ?? 0) / step));
    const slidesPerClick = Math.max(1, visible - 1);

    return step * slidesPerClick;
  }

  handlePrevious = () => this.advance(-1);

  handleNext = () => this.advance(1);

  /** @param {number} direction -1 for previous, 1 for next */
  advance(direction) {
    if (!this.track) return;
    this.track.scrollBy({ left: this.scrollDistance * direction, behavior: 'smooth' });
  }

  updateControls = () => {
    if (!this.track) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 1;

    // `scrollLeft` is negative in RTL contexts; comparing against the magnitude keeps the logic correct.
    const distanceFromStart = Math.abs(scrollLeft);

    if (this.nav) this.nav.hidden = !hasOverflow;
    if (this.previousButton) this.previousButton.disabled = !hasOverflow || distanceFromStart <= 1;
    if (this.nextButton) this.nextButton.disabled = !hasOverflow || distanceFromStart >= maxScroll - 1;
  };
}

if (!customElements.get('new-arrivals-carousel')) {
  customElements.define('new-arrivals-carousel', NewArrivalsCarousel);
}
